import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { usePatientRiskGapsWithUIExclusions } from '@ctw/shared/api/fhir/risk-gaps';
import { withOverviewCardErrorBoundary } from '@ctw/shared/content/overview/with-overview-card-error-boundary';
import { PatientRiskGapsResourceTableRow } from '@ctw/shared/content/patient-risk-gaps/components/resource-table';
import { buildPatientRiskGapsColumnsV2 } from '@ctw/shared/content/patient-risk-gaps/helpers/columns-v2';
import { useNavigateToRiskGapsV2 } from '@ctw/shared/content/patient-risk-gaps/helpers/risk-gaps-search-params';
import { buildRiskGapTelemetryContext } from '@ctw/shared/content/risk/utils';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useFilteredSortedData } from '@ctw/shared/hooks/use-filtered-sorted-data';
import { tw } from '@ctw/shared/utils/tailwind';
import { faStarExclamation } from '@fortawesome/pro-solid-svg-icons';
import { useMemo } from 'react';
import { Badge } from '../../components/badge';
import {
  ResourceOverviewCard,
  type SpecificResourceOverviewCardProps,
} from '../overview/resource-overview-card';
import { ResourceTitleColumn } from '../resource/resource-title-column';
import { riskGapStatusBadgeDefinition } from '../risk/risk-gap-status-badge-definition';
import { defaultRiskSort } from './helpers/sorts';
import { RiskGapView } from './helpers/views';

export interface PatientRiskGapOverviewProps extends SpecificResourceOverviewCardProps {
  onAfterDetailsDrawerClosed?: () => void;
  RiskGapIdForDetailsDrawer?: string;
  onOpenDetailsDrawer?: (RiskGap: RiskGapModel) => void;
}

const RISK_GAP_OVERVIEW_COLUMNS = buildPatientRiskGapsColumnsV2();

export const PatientRiskGapOverview = withOverviewCardErrorBoundary({
  cardTitle: 'Risk Capture',
  boundaryName: 'PatientRiskGapOverview',
  Component: ({ onSeeAllResources, onOpenDetailsDrawer }: PatientRiskGapOverviewProps) => {
    const riskCaptureWorkflowPreviewEnabled = useFeatureFlag('ui-riskcapture-workflow-preview');
    const telemetry = useTelemetry();
    const navigateToRiskGapV2 = useNavigateToRiskGapsV2();

    const { openResourceDrawer } = useResourceDrawer();
    const { data, isLoading } = usePatientRiskGapsWithUIExclusions();

    const currentYearRiskGaps = useMemo(() => {
      if (!data) {
        return [];
      }
      return data.filter((riskGap) => {
        const periodYear = riskGap.period;
        return periodYear === new Date().getFullYear().toString();
      });
    }, [data]);

    const { data: filteredSortedData } = useFilteredSortedData({
      defaultSort: defaultRiskSort,
      viewOptions: [RiskGapView.active],
      records: currentYearRiskGaps,
      defaultView: RiskGapView.active.display,
    });

    if (riskCaptureWorkflowPreviewEnabled) {
      return (
        <ResourceOverviewCard
          onRowClick={({ record }) => {
            telemetry.trackInteraction(
              'risk_gap_overview_card_row_click',
              buildRiskGapTelemetryContext(record),
            );
            navigateToRiskGapV2(record);
          }}
          data={filteredSortedData}
          emptyState={
            <div className={tw`flex flex-col items-center justify-center gap-5`}>
              <div>
                {data?.length === 0
                  ? 'We didn’t find any suspect conditions or recapture opportunities for this patient'
                  : 'There are no active suspect conditions or recapture opportunities for this patient'}
              </div>
            </div>
          }
          footerCTA={
            data?.length === 0
              ? undefined
              : {
                  label: 'All Risk Capture',
                  onClick: onSeeAllResources,
                }
          }
          title="Risk Capture"
          helpText="Risk suspecting and recapture opportunities for the current calendar year"
          loading={isLoading}
          headerIcon={faStarExclamation}
          testId="risk-gaps-overview"
          rowClassName={tw`p-0`}
          renderResource={(record: RiskGapModel, index: number) => (
            <PatientRiskGapsResourceTableRow
              variant={'active'}
              title={'overview'}
              record={record}
              rowIndex={index}
              displayedRecords={filteredSortedData}
              // The ResourceOverviewCard provides its own background hover.
              hasBackgroundHover={false}
              shouldMinimize={true}
              columns={RISK_GAP_OVERVIEW_COLUMNS}
            />
          )}
        />
      );
    }

    return (
      <ResourceOverviewCard
        onRowClick={({ telemetry: rowTelemetry, record: clickedRecord }) => {
          onOpenDetailsDrawer?.(clickedRecord);
          openResourceDrawer({
            telemetry: rowTelemetry,
            telemetryContext: {
              riskGapType: clickedRecord.type,
              riskGapStatus: clickedRecord.status,
              riskGapSubstatuses: clickedRecord.substatuses,
              riskGapConfidence: clickedRecord.confidenceLevel,
            },
            model: clickedRecord,
          });
        }}
        data={filteredSortedData}
        emptyState={
          data?.length === 0 ? (
            'We didn’t find any suspect conditions or recapture opportunities for this patient'
          ) : (
            <div className={tw`flex flex-col items-center justify-center gap-5`}>
              <div>
                There are no active suspect conditions or recapture opportunities for this patient
              </div>
            </div>
          )
        }
        footerCTA={
          data?.length === 0
            ? undefined
            : {
                label: 'All Risk Capture',
                onClick: onSeeAllResources,
              }
        }
        title="Risk Capture"
        helpText="Risk suspecting and recapture opportunities for the current calendar year"
        loading={isLoading}
        headerIcon={faStarExclamation}
        testId="risk-gaps-overview"
        renderResource={(record: RiskGapModel) => (
          <div className={tw`flex justify-between text-left`}>
            <div className={tw`w-full flex-grow text-sm`}>
              <ResourceTitleColumn
                className={tw`w-full`}
                titleClassName={tw`group-hover:no-underline`}
                title={
                  <div className={tw`flex h-full w-full flex-col gap-[3px]`}>
                    <div className={tw`flex w-full items-start justify-between`}>
                      <div
                        className={tw`w-full font-medium text-content-main text-sm group-hover:underline`}
                      >
                        {record.title}
                      </div>
                      <Badge
                        className={tw`mx min-w-40 justify-end text-wrap`}
                        {...riskGapStatusBadgeDefinition(record)}
                      />
                    </div>
                    <div className={tw`font-normal text-content-subtle text-sm`}>
                      {record.lastDiagnosedDisplay && <>Last Dx {record.lastDiagnosedDisplay}</>}
                    </div>
                  </div>
                }
              />
            </div>
          </div>
        )}
      />
    );
  },
});
